import React, { useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Formik, useFormikContext, yupToFormErrors } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import { utcToZonedTime } from 'date-fns-tz';
import _differenceWith from 'lodash-es/differenceWith';
import { v4 as uuidv4 } from 'uuid';
import { preBindMVRStates, policyType, FarmRiskType, StructureCategory } from '@ourbranch/lookups';
import {
  coverageRSExtendedForcedStates,
  coverageRSForcedStates,
  coverageRSForcedAtXYearsStates
} from '@ourbranch/roof-helpers';
import * as Sentry from '@sentry/react';

import useSession from 'core/hooks/use-session';
import { getMVRCheckStatus } from 'core/helpers/mvr-helpers';
import { FormAction } from 'core/store/offer-store';
import { stringDateToAwsDate, awsDateToDateObject, dateFormatter, cleanObject } from 'core/helpers/formatters';
import { getValues, formatSPP, sortItems } from 'core/helpers/scheduled-pp-helper';
import { types } from 'core/helpers/sanitize';
import { track } from 'core/helpers/analytics';
import { doesArrayHaveNewItems } from 'core/helpers/equality-checker';
import { useStore } from 'core/store';
import LayoutWithSidebar from 'core/components/layout-with-sidebar';
import { useToast } from 'core/components/toast';
import { formatUmbrellaValues } from 'common/components/umbrella/umbrella';
import { haveAnyOfThisCarsSymbolsChanged } from 'common/helpers/car-symbols-helpers';
import {
  formatValues as formatHomeValues,
  getHighestEducationAppCoApp,
  getOldestAppCoApp
} from 'common/components/home/detail/helper';
import { getPrimaryOrBlankMortgageDetails, updatePrimaryMortgageDetails } from 'offer/helpers/mortgage-helpers';
import { canOfferMegaDownPay, setMegaDownPayOnOffer } from 'offer/helpers/mega-down-pay-helpers';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import Offer from 'offer/offer';
import { OfferHeader, OfferSidebar } from 'offer';
import { buildSchema } from './offer.validation-schema';
// Fix: Import validation utilities for initial values construction
import { formatCondoValues } from 'common/components/condo/detail/helper';
import { DisableProvider, useDisabledState } from 'common/disabled-context';
import { brandConfig } from 'brand-config';
import { hydrateFarmPartyForForm, prepareFarmPartyForSave } from 'common/helpers/farm-interested-party-helpers';
import {
  michiganPipInitialValuesHelper,
  getPolicyLimitPIPWLInitialValue,
  michiganPipISubmitValuesHelper,
  getPolicyLimitPIPWLSubmitValue
} from 'common/components/personal-injury-protection/helpers';

// Component to disable the form when farm offer is waiting for underwriting approval
const FarmUnderwritingLock = observer(({ children }) => {
  const { offer: store } = useStore();
  const { setDisabled } = useDisabledState();
  const session = useSession();

  const approvalDetails = store.offer?.quote?.preBindUWCheck?.approvalDetails;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const farmWaitingForApproval = sentToUnderwritingAt && !underwriterApprovedAt;

  useEffect(() => {
    if (farmWaitingForApproval && !session.canApproveUWReview) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [farmWaitingForApproval, session.canApproveUWReview, setDisabled]);

  return children;
});

// Syncs frontend-only farm toggle states (useFarmLocationApplicantAddress, useApplicantAddress)
// to a ref so they survive Formik's enableReinitialize when the backend doesn't return them
const FarmToggleSync = ({ toggleStatesRef }) => {
  const { values } = useFormikContext();
  useEffect(() => {
    values.farmLocations?.forEach((location, locIdx) => {
      if (location.useFarmLocationApplicantAddress !== undefined) {
        toggleStatesRef.current.set(`loc_${locIdx}`, location.useFarmLocationApplicantAddress);
      }
      location.risks?.forEach((risk, riskIdx) => {
        if (risk.useApplicantAddress !== undefined) {
          toggleStatesRef.current.set(`loc_${locIdx}_risk_${riskIdx}`, risk.useApplicantAddress);
        }
      });
    });
  });
  return null;
};

const OfferProvider = observer(({ offerId }) => {
  const history = useHistory();
  const toast = useToast();
  const urlParams = useParams();

  // Clean the offerId from URL params to handle trailing punctuation
  const cleanedOfferId = extractCleanOfferId(urlParams.offerId);

  const session = useSession();
  const {
    user: { username },
    canEditUneditablePropertyDetailsFields
  } = session;
  if (!cleanedOfferId) {
    throw new Error('Invalid URL');
  }
  const {
    offer: store,
    matchSearch: { setShowCustomerMatches },
    affinityLookups
  } = useStore();
  const selectedOption = store?.selectedOption || '';

  let mortgageDetails = [];
  if (selectedOption) {
    if (selectedOption.includes(policyType.Home)) {
      mortgageDetails = store?.offer?.quote?.home?.mortgageDetails;
    } else if (selectedOption.includes(policyType.Condo)) {
      mortgageDetails = store?.offer?.quote?.condo?.mortgageDetails;
    } else if (selectedOption.includes(policyType.Farm)) {
      mortgageDetails = store?.offer?.quote?.farm?.mortgageDetails;
    }
  }
  const primaryMortgageDetail = getPrimaryOrBlankMortgageDetails(mortgageDetails);

  const affinity = store?.offer?.quote?.global?.affinity;
  const affinityData = affinityLookups.data.find((aff) => aff.affinity.startsWith(affinity));
  const agencyAffinity = affinity && affinityData?.isAgency;
  // if agency affinity and internal agent is binding for them, keep agency rep as sales rep
  const dontOverwriteRep = Boolean(!session.isAgency && agencyAffinity);

  // Ref to preserve frontend-only farm toggle states across enableReinitialize
  const farmToggleStatesRef = useRef(new Map());

  // Helper function to clean numeric fields that might be NaN
  const cleanNumericField = (value, defaultValue = 0) => {
    if (Number.isNaN(value)) {
      return defaultValue;
    }
    return value ?? defaultValue;
  };

  const initialValues = {
    ...store?.offer?.quote,
    ...(store.offer?.rejections
      ? { forcedMultiPolicyDiscount: store.offer.quote.forcedMultiPolicyDiscount ?? 'N' }
      : {}),
    home: store?.shouldShowHomeTab ? store?.offer?.quote.home : null,
    condo: store?.shouldShowCondoTab
      ? {
          ...(store?.offer?.quote?.condo ?? {}),
          noUnitNumber: !store.offer?.quote?.condo?.hasUnitNumber,
          monthsRentedOut: store?.offer?.quote?.condo?.weeksRentedOut
            ? Math.floor(store?.offer?.quote?.condo?.weeksRentedOut / 4)
            : 0
        }
      : null,
    auto: {
      ...store?.offer?.quote?.auto,
      pipAdditionalResidents: store?.offer?.quote?.auto?.pipAdditionalResidents || 0
    },
    drivers: store?.offer?.quote?.drivers,
    autoCoverage: store?.shouldShowAutoTab
      ? {
          ...store?.offer?.quote?.autoCoverage,
          policyLimitPIPWL: getPolicyLimitPIPWLInitialValue(
            store.state,
            store?.offer?.quote?.autoCoverage.policyLimitPIPWL,
            store?.offer?.quote?.drivers,
            store?.offer?.quote?.drivers.concat(store?.offer?.quote?.nonDrivers)
          ),
          policyLimitPIPACR: michiganPipInitialValuesHelper(
            store.state,
            'policyLimitPIPACR',
            store?.offer?.quote?.autoCoverage.policyLimitPIPACR
          ),
          policyLimitPIPME: michiganPipInitialValuesHelper(
            store.state,
            'policyLimitPIPME',
            store?.offer?.quote?.autoCoverage.policyLimitPIPME,
            store?.offer?.quote?.drivers.concat(store?.offer?.quote?.nonDrivers)
          )
        }
      : undefined,
    condoCoverage: store?.shouldShowCondoTab ? store?.offer?.quote?.condoCoverage : {},
    farmLocations: store?.isFarmOffer
      ? store?.offer?.quote?.farmLocations?.map((location, locIdx) => ({
          ...location,
          // Preserve frontend-only toggle state across enableReinitialize (not returned by backend)
          useFarmLocationApplicantAddress:
            location.useFarmLocationApplicantAddress ?? farmToggleStatesRef.current.get(`loc_${locIdx}`) ?? true,
          risks: location.risks?.map((risk, riskIdx) => {
            // Check if brand requires cosmetic damage auto-exclusion (from brand config)
            const shouldAutoExcludeCosmeticDamage = brandConfig.features?.shouldAutoExcludeCosmeticDamage || false;

            // Fix: Apply cosmetic damage exclusion business logic during initial values construction
            // This ensures the initial values match what useEffect hooks will set, preventing dirty state
            let cosmeticDamageExclusion = risk.coverages?.cosmeticDamageExclusion;
            let exteriorDoorsWindowsDamageExclusion = risk.coverages?.exteriorDoorsWindowsDamageExclusion;
            let exteriorWallsDamageExclusion = risk.coverages?.exteriorWallsDamageExclusion;
            let roofsDamageExclusion = risk.coverages?.roofsDamageExclusion;

            // 1. FO6 Business Logic: Auto-enable all exclusions for FO6 structures
            if (risk.type === FarmRiskType.Fo6Structures) {
              cosmeticDamageExclusion = true;
              exteriorDoorsWindowsDamageExclusion = true;
              exteriorWallsDamageExclusion = true;
              roofsDamageExclusion = true;
            }

            // 2. Brand Logic: Auto-enable all exclusions when brand constant indicates we should
            if (shouldAutoExcludeCosmeticDamage) {
              cosmeticDamageExclusion = true;
              exteriorDoorsWindowsDamageExclusion = true;
              exteriorWallsDamageExclusion = true;
              roofsDamageExclusion = true;
            }

            // 3. ACV Logic: Auto-enable all exclusions when ACV loss settlement and brand constant indicates we should
            if (
              shouldAutoExcludeCosmeticDamage &&
              (risk.coverages?.roofLossSettlement === 'ACV' || risk.coverages?.lossSettlement === 'ACV')
            ) {
              cosmeticDamageExclusion = true;
              exteriorDoorsWindowsDamageExclusion = true;
              exteriorWallsDamageExclusion = true;
              roofsDamageExclusion = true;
            }

            // Pre-apply address from farm location when useApplicantAddress is true
            // This matches the useEffect in FO4.jsx that syncs risk address with location address
            const resolvedUseApplicantAddress =
              risk.useApplicantAddress ?? farmToggleStatesRef.current.get(`loc_${locIdx}_risk_${riskIdx}`) ?? true;
            const preAppliedAddress =
              resolvedUseApplicantAddress && location.address
                ? {
                    address: location.address.address,
                    address2: location.address.address2,
                    city: location.address.city,
                    state: location.address.state,
                    zip: location.address.zip,
                    county: location.address.county
                  }
                : risk.address;

            if (risk.type === FarmRiskType.Fo6Structures && risk.coverages && risk.building) {
              const isBarnOutbuilding = risk.building?.buildingType === StructureCategory.BarnsOutbuildings;
              const buildingClass = risk.building?.buildingClass;

              if (isBarnOutbuilding && buildingClass) {
                let requiredLossSettlement = risk.coverages.structureLossSettlement;
                let requiredSpecialForm = risk.coverages.specialFormCoverage;
                let requiredSnowWeight = risk.coverages.hasSnowWeightCoverage;

                // Apply building class requirements
                if (buildingClass === 1 || buildingClass === 2) {
                  requiredLossSettlement = 'RC';
                  requiredSpecialForm = true;
                  requiredSnowWeight = true;
                } else if (buildingClass === 3) {
                  requiredLossSettlement = 'ACV';
                  requiredSpecialForm = false;
                  requiredSnowWeight = false;
                }

                return {
                  ...risk,
                  useApplicantAddress: resolvedUseApplicantAddress,
                  address: preAppliedAddress,
                  adjustmentFactor: cleanNumericField(risk.adjustmentFactor, 0),
                  coverages: {
                    ...risk.coverages,
                    // Pre-apply the building class requirements to prevent dirty state
                    structureLossSettlement: requiredLossSettlement,
                    specialFormCoverage: requiredSpecialForm,
                    hasSnowWeightCoverage: requiredSnowWeight,
                    // Pre-apply cosmetic damage exclusion logic to prevent dirty state
                    cosmeticDamageExclusion,
                    exteriorDoorsWindowsDamageExclusion,
                    exteriorWallsDamageExclusion,
                    roofsDamageExclusion
                  }
                };
              }
            }

            return {
              ...risk,
              // Preserve frontend-only toggle state across enableReinitialize (not returned by backend)
              useApplicantAddress: resolvedUseApplicantAddress,
              address: preAppliedAddress,
              adjustmentFactor: cleanNumericField(risk.adjustmentFactor, 0),
              coverages: {
                ...risk.coverages,
                // Pre-apply cosmetic damage exclusion logic to prevent dirty state
                cosmeticDamageExclusion,
                exteriorDoorsWindowsDamageExclusion,
                exteriorWallsDamageExclusion,
                roofsDamageExclusion
              }
            };
          })
        }))
      : [],
    farmCoverage: store?.isFarmOffer
      ? {
          ...store?.offer?.quote?.farmCoverage,
          policyLevelAdjustmentFactor: cleanNumericField(
            store?.offer?.quote?.farmCoverage?.policyLevelAdjustmentFactor,
            0
          )
        }
      : {},
    includeFarmLiability: store?.offer?.quote?.includeFarmLiability,
    farmLiabilityCoverage: store?.isFarmOffer ? { ...store?.offer?.quote?.farmLiabilityCoverage } : {},
    farmInterestedParties: store?.isFarmOffer
      ? (store?.offer?.quote?.farmInterestedParties || []).map(hydrateFarmPartyForForm)
      : [],
    selectedOption: selectedOption || store?.defaultSelectedOption,
    primaryMortgageDetail,
    highestEducation:
      store?.offer?.quote?.people || store?.offer?.quote?.drivers
        ? getHighestEducationAppCoApp(store?.offer?.quote?.people || store?.offer?.quote?.drivers)
        : undefined,
    oldestResident:
      store?.offer?.quote?.people || store?.offer?.quote?.drivers
        ? getOldestAppCoApp(store?.offer?.quote?.people || store?.offer?.quote?.drivers)
        : undefined,
    scheduledPersonalProperty: {
      deductible: store?.offer?.quote?.scheduledPersonalProperty?.items
        ? store?.offer?.quote?.scheduledPersonalProperty?.deductible
        : null,
      items: store?.offer?.quote.scheduledPersonalProperty?.items.length
        ? sortItems(store.offer.quote.scheduledPersonalProperty.items.map((item) => getValues(item)))
        : []
    },
    umbrellaCoverage:
      store?.offer?.quote?.includeUmbrella &&
      ([policyType.HABundle, policyType.CABundle].includes(store.selectedOption) ||
        ([policyType.Home, policyType.Condo].includes(store.selectedOption) &&
          store.offer.quote.existingCustomer?.length > 0))
        ? {
            watercraftHullLengths: [],
            rentalPropertyAddresses: [],
            otherPropertyAddresses: [],
            numRVs: null,
            numMotorcyclesScooters: null,
            numVehiclesTotal: null,
            numLicensedDriversUnder25: null,
            numATVs: null,
            numPersonalWatercraft: null,
            liabilityCoverageLimit: null,
            ...cleanObject({ ...store?.offer?.quote?.umbrellaCoverage })
          }
        : null,
    dontOverwriteRep,
    accountIdMatchField: null,
    global: store?.offer?.quote?.global
      ? {
          ...store.offer.quote.global,
          fromRC1: !!store.offer.usingNonVerifiedData,
          fromApi: false,
          affinity: store.offer.quote.global.affinity ?? '',
          mostRecentAutoInsuranceCoverageEndDate:
            store.offer.quote.global?.currentAutoCarrierEndDate ||
            store.offer.quote.global?.mostRecentAutoInsuranceCoverageEndDate
        }
      : null
  };

  useEffect(() => {
    if (cleanedOfferId !== store?.offer?.id) {
      store.getOffer(cleanedOfferId);
    }
  }, [cleanedOfferId, store.getOffer, store?.offer?.id]);

  // this reaction tracks the loading state, and when it changes (true/false), checks if the offer was set correctly
  // essentially handling cases were access to an offer is unauthorized
  // or the offer fails to load/set in the store for any reason
  reaction(
    () => store.loading,
    (loading) => {
      if (!loading && !store.offer) {
        toast.notify({
          type: 'error',
          message: `An Error was encountered. Error: ${store.errors[0]?.message}. Please contact ${
            session.isAgency ? 'Agency Support' : 'the Help Desk'
          }.`,
          durationInSeconds: 10
        });
        setTimeout(() => {
          history.push('/search/offers');
        }, 7000);
      }
    }
  );

  // Recalculate the offer market tier rating whenever offer changes
  reaction(() => store.offer, store.setOfferMarketTier);

  const validate = useCallback(
    async (values) => {
      try {
        const { hasOnlyMVRToRun, hasAtLeastMVRToPull } = getMVRCheckStatus(store, false, values);
        const schema = await buildSchema({
          formAction: store.formAction,
          includeConnectedHome: store.includeConnectedHome,
          isAdvancedConnectedHome: store.isAdvancedConnectedHome,
          needMVRs: preBindMVRStates.includes(store.state) && values.drivers?.some((driver) => driver.postBindMVR),
          session,
          selectedOption: values.selectedOption,
          state: store.state,
          messageContext: { ...values },
          initialValuesContext: initialValues,
          rejections: store.offer?.rejections
        });

        await schema.validate(values, {
          abortEarly: false,
          context: {
            ...values,
            state: store.state,
            initialValues,
            canAddCarsManually: session.canAddCarsManually,
            canEditCoverageA: session.canEditCoverageA,
            affinityLookups,
            hasOnlyMVRToRun,
            hasAtLeastMVRToPull,
            formAction: store.formAction,
            offerId: store.offer?.id
          }
        });
      } catch (error) {
        const formErrors = yupToFormErrors(error);
        console.log({ formErrors });

        if (error?.name?.includes('TypeError')) {
          console.error(error);
          Sentry.captureException(error, {
            extra: {
              formValues: values,
              inOfferEdit: store.formAction === FormAction.Update,
              inOfferGoToCheckout: store.formAction === FormAction.Checkout,
              validationFailedToRun: true,
              formAction: store.formAction
            }
          });
        } else {
          return formErrors;
        }
      }
    },
    [store, session, affinityLookups]
  );

  const submitRecalculate = async (details) => {
    const residents = details?.drivers.concat(details?.nonDrivers || []);
    const homeCoverage = {};

    const formattedMortgageDetails = updatePrimaryMortgageDetails(
      details?.home?.mortgageDetails || details?.condo?.mortgageDetails,
      details.primaryMortgageDetail,
      details?.global?.homeownersPaymentMethod
    );

    const roofAge = new Date().getFullYear() - details?.home?.roofYear;
    const isArkansasNewRoof = store.state === 'AR' && roofAge <= coverageRSForcedAtXYearsStates[store.state];
    const coverageRSExtendedForced = coverageRSExtendedForcedStates[store.state] || isArkansasNewRoof;
    if (coverageRSExtendedForced && !details?.homeCoverage?.coverageRSExtended) {
      homeCoverage.coverageRSExtended = true;
    }
    details.drivers.forEach((d) => {
      if (d?.clearUDRViolation) {
        d.gotDrivingRecord = true;
      }
    });

    if (coverageRSForcedStates[store.state] && !!details?.homeCoverage?.coverageRSExtended) {
      homeCoverage.coverageRSExtended = false;
    }

    const driversToRecheck = store.driverIdsToRecheck.map((driverId) => {
      const driverToRecheck = details.drivers.find((driver) => driver.id === driverId);
      const oldDriverIndex = details.drivers.indexOf(driverToRecheck);
      driverToRecheck.id = uuidv4();
      details.drivers.splice(oldDriverIndex, 1);

      return types.PersonDetailsInput(driverToRecheck);
    });

    if (details.selectedOption === policyType.CABundle) {
      details.condo.numVehicles = details.cars.length;
    }

    const primaryApplicant = details.drivers?.find((d) => d.isPrimary);

    // remove MPD if no existing customer linked
    const forcedMultiPolicyDiscount =
      !details.existingCustomer?.id && details.forcedMultiPolicyDiscount && details.forcedMultiPolicyDiscount !== 'N'
        ? 'N'
        : details.forcedMultiPolicyDiscount;

    let formattedDetails = {
      ...details,
      forcedMultiPolicyDiscount,
      // an offer could be Auto&Renters, or it could be Auto w/ renters added
      includeRenters: (selectedOption.includes('R') || selectedOption.includes('A')) && details.includeRenters,
      fname: primaryApplicant?.firstName ?? details.fname,
      lname: primaryApplicant?.lastName ?? details.lname,
      condo: store?.shouldShowCondoTab
        ? { ...formatCondoValues(details.condo), mortgageDetails: formattedMortgageDetails }
        : undefined,
      connectedHome: store.includeConnectedHome ? details.connectedHome : null,
      fromStaff: true,
      rep: details.dontOverwriteRep && details.rep?.length > 0 ? details.rep : username,
      umbrellaCoverage: formatUmbrellaValues(details.umbrellaCoverage, details.includeUmbrella),
      scheduledPersonalProperty: formatSPP(details.scheduledPersonalProperty),
      home: store?.shouldShowHomeTab
        ? { ...formatHomeValues(details.home), mortgageDetails: formattedMortgageDetails }
        : undefined,
      rentersCoverage: details.includeRenters && details.rentersCoverage ? details.rentersCoverage : undefined,
      auto:
        store?.shouldShowAutoTab && details?.auto
          ? store.state === 'MI'
            ? {
                ...details.auto,
                pipAllResidents: residents.length,
                pipAdditionalResidents: residents.filter((resident) => resident?.pipOptOut)?.length || 0,
                pipExcludedResidents: residents.filter((resident) => resident?.pipOptOut)?.length || 0,
                pipResidentsWithQHC: residents.filter((resident) => resident?.pipHasQHC)?.length || 0
              }
            : { ...details.auto }
          : undefined,
      autoCoverage:
        store?.shouldShowAutoTab && details?.autoCoverage
          ? store.state === 'MI'
            ? {
                ...details.autoCoverage,
                policyLimitPIPWL: getPolicyLimitPIPWLSubmitValue(
                  store.state,
                  details?.autoCoverage.policyLimitPIPWL,
                  details?.drivers,
                  residents
                ),
                policyLimitPIPACR: michiganPipISubmitValuesHelper(
                  store.state,
                  'policyLimitPIPACR',
                  details?.autoCoverage.policyLimitPIPACR
                ),
                policyLimitPIPME: michiganPipISubmitValuesHelper(
                  store.state,
                  'policyLimitPIPME',
                  details?.autoCoverage.policyLimitPIPME,
                  residents
                )
              }
            : { ...details.autoCoverage }
          : undefined,
      homeCoverage: details?.homeCoverage ? { ...details.homeCoverage, ...homeCoverage } : null
    };

    if (
      details.selectedOption?.includes(policyType.Home) &&
      details?.home?.roofYear !== initialValues?.home?.roofYear
    ) {
      formattedDetails = { ...formattedDetails, home: { ...formattedDetails.home, roofYearChangedManually: true } };
    }

    if (details.selectedOption?.includes(policyType.Farm)) {
      formattedDetails.farmLocations = details.farmLocations;
      formattedDetails.farmCoverage = details.farmCoverage;
      formattedDetails.includeFarmLiability = details.includeFarmLiability;
      formattedDetails.farmLiabilityCoverage = details.farmLiabilityCoverage;
      formattedDetails.entity = details.entity;

      // Convert flat chipArray arrays → associatedEntities before sanitizer strips them
      if (formattedDetails.farmInterestedParties) {
        formattedDetails.farmInterestedParties = formattedDetails.farmInterestedParties.map(prepareFarmPartyForSave);
      }

      // Format schedule coverages for each location
      formattedDetails.farmLocations.forEach((location) => {
        location.risks &&
          location.risks.forEach((risk) => {
            // Sanitize extendedDetails for scheduleCoverage items
            if (risk.coverages?.scheduleCoverages && Array.isArray(risk.coverages?.scheduleCoverages)) {
              risk.coverages.scheduleCoverages.forEach((coverage) => {
                if (coverage.items) {
                  coverage.items = coverage.items.map((item) => types.FarmScheduledItemInput(item));
                }
              });
            } else {
              // Backend expects an empty array if no schedule coverages are provided
              if (risk.coverages) {
                risk.coverages.scheduleCoverages = [];
              }
            }
            // Sanitize swimming pool input for farm dwelling buildings
            if (risk.building?.swimmingPool) {
              risk.building.swimmingPool = types.SwimmingPoolInput(risk.building.swimmingPool);
            }
          });
      });
    }

    formattedDetails = types.QuoteDetailsInput(formattedDetails);

    track('Staff Modify Offer', { offer: { ...formattedDetails } });
    if (
      formattedDetails?.home &&
      store?.offer?.quote?.home?.originalSqFt &&
      formattedDetails?.home.sqFt !== store?.offer?.quote?.home?.originalSqFt
    ) {
      track('Above-ground sq. ft. different than data-provided value', {
        isAgency: session.isAgency,
        AboveGroundSqFtValue: formattedDetails?.home.sqFt,
        OriginalAboveGroundSqFtValue: store?.offer?.quote?.home?.originalSqFt
      });
    }

    // check if we need to call rater the event field that also will add drivers/cars
    const shouldAddDrivers = doesArrayHaveNewItems(store.offer.quote.drivers, details.drivers);
    const shouldAddCars = doesArrayHaveNewItems(store.offer.quote.cars, details.cars);
    const shouldAddTrailers = doesArrayHaveNewItems(store.offer.quote.trailers, details.trailers);

    if (shouldAddDrivers || shouldAddCars || shouldAddTrailers || driversToRecheck.length >= 1) {
      // format data to all be same shape so the lodash differenceWith method works properly
      const driversIn = details.drivers.map((driver) => {
        return types.PersonDetailsInput(driver);
      });
      const savedDriversOnOffer = store.offer.quote.drivers.map((driver) => {
        return types.PersonDetailsInput(driver);
      });

      // for new/existing drivers,cars & trailers use the input subtracting the new items, otherwise you won't capture any changes to existing items.

      const newDrivers = _differenceWith(
        driversIn,
        savedDriversOnOffer,
        (driverIn, existingDriver) => driverIn.id === existingDriver.id
      ).map((newDriver) => {
        return types.PersonDetailsInput(newDriver);
      });

      const existingDrivers = _differenceWith(
        details.drivers,
        [...newDrivers, ...driversToRecheck],
        (existingDriver, driverIn) => existingDriver.id === driverIn.id
      ).map((driver) => {
        // Merge form data over the store's driver data so that if the form is missing fields
        // (e.g. dateOfBirth lost due to form state), the store's values serve as a fallback.
        // Only non-null/non-empty form values overwrite; null/undefined/'' are ignored.
        // See: https://github.com/branch-insurance/branch/issues/29285
        const storeDriver = store.offer.quote.drivers?.find((d) => d.id === driver.id);
        if (storeDriver) {
          const definedFormFields = Object.fromEntries(Object.entries(driver).filter(([, v]) => v != null && v !== ''));
          return types.PersonDetailsInput({ ...storeDriver, ...definedFormFields });
        }
        return types.PersonDetailsInput(driver);
      });

      const hasSymbols = (car, initialValuesCars) => haveAnyOfThisCarsSymbolsChanged(car, initialValuesCars);
      if (details.cars?.some((car) => hasSymbols(car))) {
        details.cars.forEach((car) => {
          if (hasSymbols(car, initialValues.cars)) {
            car.manuallyAddedSymbols = true;
            car.symbolMake = car.symbolMake?.toUpperCase();
            car.symbolModel = car.symbolModel?.toUpperCase();
            car.symbolStyle = car.symbolStyle?.toUpperCase();
            car.symbolAux = car.symbolAux?.toUpperCase();
            car.symbolPGS = car.symbolPGS?.toUpperCase() || ' ';
          }
        });
      }

      const newCars = _differenceWith(details.cars, store.offer.quote.cars, (a, b) => a.VIN === b.VIN).map((newCar) => {
        return types.CarDetailsInput(newCar);
      });

      const existingCars = _differenceWith(details.cars, newCars, (a, b) => a.VIN === b.VIN).map((car) => {
        return types.CarDetailsInput(car);
      });

      const newTrailers = _differenceWith(details.trailers, store.offer.quote.trailers, (a, b) => a.VIN === b.VIN).map(
        (newTrailer) => {
          return types.TrailerDetailsInput(newTrailer);
        }
      );

      const existingTrailers = _differenceWith(details.trailers, newTrailers, (a, b) => a.VIN === b.VIN).map(
        (newTrailer) => {
          return types.TrailerDetailsInput(newTrailer);
        }
      );

      if (selectedOption === policyType.CABundle) {
        formattedDetails.condo.numVehicles = existingCars.length;
      }

      if (selectedOption.includes(policyType.Auto) && store.state === 'MI') {
        const totalResidents =
          [...newDrivers, ...driversToRecheck].length + existingDrivers.length + (details.nonDrivers || []).length;
        formattedDetails.auto.pipAllResidents = totalResidents;
        if (totalResidents === 1) {
          formattedDetails.auto.pipEveryoneOnSamePlan = true;
        }
      }

      if (
        details.global?.autoMegaDownPay ||
        details.global?.homeMegaDownPay ||
        details.global?.rentersMegaDownPay ||
        details.global?.condoMegaDownPay
      ) {
        const newGlobal = setMegaDownPayOnOffer(details);
        formattedDetails = types.QuoteDetailsInput({ ...formattedDetails, global: newGlobal });
      }

      await store.addDriversAddCarsAndRecalculateCluster({
        newDrivers: [...newDrivers, ...driversToRecheck],
        newVins: newCars,
        newTrailerVins: newTrailers,
        revisedQuoteDetails: {
          ...formattedDetails,
          drivers: existingDrivers,
          cars: existingCars,
          trailers: existingTrailers
        },
        offerId: store.offer.id,
        history
      });

      if (store.hasOneOrMoreUDRs) {
        toast.notify({ type: 'error', message: "A driver's driving record could not be verified." });
      }
    } else {
      /*
      if the effective dates are not today, we calculate new effective date (this operation also updates the rate control date)
      note, we need to do this in the same named function, or else formik will think our form has changed and reset everything
      */

      const { global } = details;
      const autoEffectiveDate = awsDateToDateObject(global.autoEffectiveDate);
      const homeEffectiveDate = awsDateToDateObject(global.homeEffectiveDate);
      const rentersEffectiveDate = awsDateToDateObject(global.rentersEffectiveDate);
      const condoEffectiveDate = global.condoEffectiveDate ? awsDateToDateObject(global.condoEffectiveDate) : null;
      const farmEffectiveDate = global.farmEffectiveDate ? awsDateToDateObject(global.farmEffectiveDate) : null;
      const now = utcToZonedTime(new Date(), store.timezone);
      const shouldUpdateAuto = !isSameDay(autoEffectiveDate, now) && !isAfter(autoEffectiveDate, now);
      const shouldUpdateHome = !isSameDay(homeEffectiveDate, now) && !isAfter(homeEffectiveDate, now);
      const shouldUpdateRenters = !isSameDay(rentersEffectiveDate, now) && !isAfter(rentersEffectiveDate, now);
      const shouldUpdateCondo =
        condoEffectiveDate && !isSameDay(condoEffectiveDate, now) && !isAfter(condoEffectiveDate, now);
      const shouldUpdateFarm =
        farmEffectiveDate && !isSameDay(farmEffectiveDate, now) && !isAfter(farmEffectiveDate, now);

      if (shouldUpdateHome || shouldUpdateAuto || shouldUpdateRenters || shouldUpdateCondo || shouldUpdateFarm) {
        const getAdjustedBillingDay = (effectiveDate, billingDay) => {
          const effectiveDay = effectiveDate.getDate();
          const shouldMoveDefaultBillingDay = billingDay === Math.min(effectiveDay === 1 ? 28 : effectiveDay - 1, 28);
          let newBillingDay = billingDay;
          if (shouldMoveDefaultBillingDay) {
            // we should move the day to the day before of the effective date
            const today = now.getDate();
            newBillingDay = Math.min(today === 1 ? 28 : today - 1, 28);
          }
          return newBillingDay;
        };

        let updatedAutoEffectiveDate = global.autoEffectiveDate;
        let updatedHomeEffectiveDate = global.homeEffectiveDate;
        let updatedRentersEffectiveDate = global.rentersEffectiveDate;
        let updatedCondoEffectiveDate = global.condoEffectiveDate;
        let updatedFarmEffectiveDate = global.farmEffectiveDate;
        let updatedAutoMegaDownPay = global.autoMegaDownPay;
        let updatedHomeMegaDownPay = global.homeMegaDownPay;
        let updatedRentersMegaDownPay = global.rentersMegaDownPay;
        let updatedCondoMegaDownPay = global.condoMegaDownPay;
        let updatedFarmMegaDownPay = global.farmMegaDownPay;
        let updatedAutoBillingDay = global.autoBillingDayOfMonth;
        let updatedHomeBillingDay = global.homeBillingDayOfMonth;
        let updatedRentersBillingDay = global.rentersBillingDayOfMonth;
        let updatedCondoBillingDay = global.condoBillingDayOfMonth;
        let updatedFarmBillingDay = global.farmBillingDayOfMonth;

        if (shouldUpdateAuto) {
          updatedAutoEffectiveDate = stringDateToAwsDate(dateFormatter(now));
          updatedAutoBillingDay = getAdjustedBillingDay(autoEffectiveDate, global.autoBillingDayOfMonth);
          updatedAutoMegaDownPay = details.global?.autoMegaDownPay
            ? canOfferMegaDownPay(updatedAutoEffectiveDate, updatedAutoBillingDay, global.autoPaymentType)
            : null;
        }
        if (shouldUpdateHome) {
          updatedHomeEffectiveDate = stringDateToAwsDate(dateFormatter(now));
          updatedHomeBillingDay = getAdjustedBillingDay(homeEffectiveDate, global.homeBillingDayOfMonth);
          updatedHomeMegaDownPay = details.global?.homeMegaDownPay
            ? canOfferMegaDownPay(updatedHomeEffectiveDate, updatedHomeBillingDay, global.homeownersPaymentType)
            : null;
        }

        if (shouldUpdateRenters) {
          updatedRentersEffectiveDate = stringDateToAwsDate(dateFormatter(now));
          updatedRentersBillingDay = getAdjustedBillingDay(rentersEffectiveDate, global.rentersBillingDayOfMonth);
          updatedRentersMegaDownPay = details.global?.rentersMegaDownPay
            ? canOfferMegaDownPay(updatedRentersEffectiveDate, updatedRentersBillingDay, global.rentersPaymentType)
            : null;
        }

        if (shouldUpdateCondo) {
          updatedCondoEffectiveDate = stringDateToAwsDate(dateFormatter(now));
          updatedCondoBillingDay = getAdjustedBillingDay(condoEffectiveDate, global.condoBillingDayOfMonth);
          updatedCondoMegaDownPay = details.global?.condoMegaDownPay
            ? canOfferMegaDownPay(updatedCondoEffectiveDate, updatedCondoBillingDay, global.condoPaymentType)
            : null;
        }

        if (shouldUpdateFarm) {
          updatedFarmEffectiveDate = stringDateToAwsDate(dateFormatter(now));
          updatedFarmBillingDay = getAdjustedBillingDay(farmEffectiveDate, global.farmBillingDayOfMonth);
          updatedFarmMegaDownPay = details.global?.farmMegaDownPay
            ? canOfferMegaDownPay(updatedFarmEffectiveDate, updatedFarmBillingDay, global.farmPaymentType)
            : null;
        }

        const newGlobal = {
          ...global,
          autoEffectiveDate: updatedAutoEffectiveDate,
          homeEffectiveDate: updatedHomeEffectiveDate,
          rentersEffectiveDate: updatedRentersEffectiveDate,
          condoEffectiveDate: updatedCondoEffectiveDate,
          farmEffectiveDate: updatedFarmEffectiveDate,
          autoMegaDownPay: updatedAutoMegaDownPay,
          homeMegaDownPay: updatedHomeMegaDownPay,
          rentersMegaDownPay: updatedRentersMegaDownPay,
          condoMegaDownPay: updatedCondoMegaDownPay,
          farmMegaDownPay: updatedFarmMegaDownPay,
          autoBillingDayOfMonth: updatedAutoBillingDay,
          homeBillingDayOfMonth: updatedHomeBillingDay,
          rentersBillingDayOfMonth: updatedRentersBillingDay,
          condoBillingDayOfMonth: updatedCondoBillingDay,
          farmBillingDayOfMonth: updatedFarmBillingDay
        };

        formattedDetails = types.QuoteDetailsInput({ ...formattedDetails, global: newGlobal });
      } else if (
        global.autoMegaDownPay ||
        global.homeMegaDownPay ||
        global.rentersMegaDownPay ||
        global.condoMegaDownPay
      ) {
        // only want to do this if we're not updating a stale offer
        // in which case the change in effective date should always a trigger
        // a change to the corresponding mega down pay node
        const newGlobal = setMegaDownPayOnOffer(details);
        formattedDetails = types.QuoteDetailsInput({ ...formattedDetails, global: newGlobal });
      }

      if (formattedDetails.correctedAddress) {
        delete formattedDetails.correctedAddress;
      }

      // if a user with this permission group enters a sqft that is less than the originalSqFt, update the originalSqFt to the new value
      // to base future validations on
      if (canEditUneditablePropertyDetailsFields && details?.home?.sqFt < details?.home?.originalSqFt) {
        formattedDetails.home.originalSqFt = details?.home?.sqFt;
      }

      // Merge dateOfBirth from the store's drivers into the formatted drivers so that if
      // the form state lost the value (e.g. due to enableReinitialize or defaultToEmpty),
      // the store's value is used as a fallback. This mirrors the existingDrivers merge
      // already applied in the add-drivers path (Path A) above.
      // See: https://github.com/gobranch/branch/issues/29388
      if (formattedDetails.drivers) {
        formattedDetails.drivers = formattedDetails.drivers.map((driver) => {
          const storeDriver = store.offer.quote.drivers?.find((d) => d.id === driver.id);
          if (storeDriver) {
            const definedFormFields = Object.fromEntries(
              Object.entries(driver).filter(([, v]) => v != null && v !== '')
            );
            return types.PersonDetailsInput({ ...storeDriver, ...definedFormFields });
          }
          return driver;
        });
      }

      await store.recalculateQuoteToCluster(store.offer.id, formattedDetails, history);
    }

    setShowCustomerMatches(false);
  };

  return (
    <DisableProvider>
      <FarmUnderwritingLock>
        <Formik
          onSubmit={submitRecalculate}
          initialValues={initialValues}
          validate={validate}
          validateOnChange={true}
          validateOnBlur
          enableReinitialize
        >
          <>
            <FarmToggleSync toggleStatesRef={farmToggleStatesRef} />
            <LayoutWithSidebar
              content={Offer}
              side={OfferSidebar}
              offerId={offerId}
              history={history}
              header={OfferHeader}
              onBack={() => history.push('/search/offers')}
            />
          </>
        </Formik>
      </FarmUnderwritingLock>
    </DisableProvider>
  );
});

export default OfferProvider;
